import type {
  AnnualTimetableCell,
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
  ProgramScopeType,
} from '../types/domain';
import { getAnnualTimetableCellsForNaturalKeys, type AnnualTimetableNaturalKey } from './annualTimetableService';
import { getProgramFixedContentRows } from './programFixedContentService';
import { getProgramProgressCellsForNaturalKeys } from './programProgressService';
import { getProgramScopedContentRows } from './programScopedContentService';
import {
  fetchPresentationAuditEntriesForProgramScope,
  type PresentationAuditEntry,
} from './programPresentationAuditService';
import {
  fetchWorksheetAuditEntriesForProgramScope,
  type WorksheetAuditEntry,
} from './programWorksheetAuditService';
import {
  loadProgramPresentationAuditSnapshotCache,
  saveProgramPresentationAuditSnapshotCache,
} from '../utils/presentationAuditSnapshotCache';

export type ProgramAuditSnapshot = {
  /** 프로그램 표 «일정» 열의 DB 원본(연간 시간표 메뉴에서 저장) */
  annualTimetableCells: AnnualTimetableCell[];
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
  /** 일정 슬롯에 해당하는 program_progress_cells(레거시·프레젠테이션 고아 조회용) */
  programProgressCells: ProgramProgressCell[];
  presentationEntries: PresentationAuditEntry[];
  worksheetEntries: WorksheetAuditEntry[];
};

export type ProgramAuditPageSnapshotParams = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  /** 현재 표에 나타나는 일정 슬롯 좌표 — 연간 시간표 셀 조회용 */
  scheduleSlotKeys: AnnualTimetableNaturalKey[];
  /** 편제(track)·프로젝트(project) 페이지: 해당 스코프의 스코프 행만 */
  scopeType?: ProgramScopeType;
  scopeKey?: string;
  /** 고정 프로그램 페이지에서만 true */
  includeFixedRows?: boolean;
};

/** 현재 프로그램 화면 범위(표 슬롯·스코프·고정행 옵션)로 DB 조회 — 데이터 정리 모달용 */
export async function fetchProgramAuditSnapshotForProgramPage(
  params: ProgramAuditPageSnapshotParams,
  options?: { forceRefresh?: boolean; scopeSignature?: string }
): Promise<ProgramAuditSnapshot> {
  const {
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    scheduleSlotKeys,
    scopeType,
    scopeKey,
    includeFixedRows,
  } = params;
  if (!workspaceId.trim() || !gradeId.trim()) {
    return {
      annualTimetableCells: [],
      scopedRows: [],
      fixedRows: [],
      programProgressCells: [],
      presentationEntries: [],
      worksheetEntries: [],
    };
  }

  const progressNaturalKeys = scheduleSlotKeys.map((k) => ({
    gradeId: k.gradeId,
    mondayIso: k.mondayIso,
    weekdayKey: k.weekdayKey,
    slotIndex: k.slotIndex,
  }));

  const sk = (scopeKey ?? '').trim();
  const scopedPromise =
    scopeType && sk
      ? getProgramScopedContentRows(workspaceId, academicYear, termNumber, gradeId, scopeType, sk, {
          forceRefresh: false,
        })
      : Promise.resolve([] as ProgramScopedContentRow[]);

  const fixedPromise = includeFixedRows
    ? getProgramFixedContentRows(workspaceId, academicYear, termNumber, gradeId, { forceRefresh: false })
    : Promise.resolve([] as ProgramFixedContentRow[]);

  /** 데이터 정리: 현재 표 슬롯에 대응하는 레거시 program_progress_cells도 항상 함께 보여준다(UI 미노출 데이터 확인용). */
  const includeLegacyProgressCells = progressNaturalKeys.length > 0;
  const programCellsPromise =
    includeLegacyProgressCells && progressNaturalKeys.length
      ? getProgramProgressCellsForNaturalKeys(workspaceId, academicYear, termNumber, gradeId, progressNaturalKeys)
      : Promise.resolve([] as ProgramProgressCell[]);

  const scopeSig = options?.scopeSignature ?? '';
  if (!options?.forceRefresh && scopeSig) {
    const cached = loadProgramPresentationAuditSnapshotCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeSig
    );
    if (cached) {
      const [annualTimetableCells, scopedRows, fixedRows, programProgressCells] = await Promise.all([
        getAnnualTimetableCellsForNaturalKeys(workspaceId, academicYear, termNumber, gradeId, scheduleSlotKeys),
        scopedPromise,
        fixedPromise,
        programCellsPromise,
      ]);
      return {
        annualTimetableCells,
        scopedRows,
        fixedRows,
        programProgressCells,
        presentationEntries: cached.presentationEntries,
        worksheetEntries: await fetchWorksheetAuditEntriesForProgramScope({
          workspaceId,
          programProgressCells,
          scopedRows,
          fixedRows,
        }),
      };
    }
  }

  const [annualTimetableCells, scopedRows, fixedRows, programProgressCells] = await Promise.all([
    getAnnualTimetableCellsForNaturalKeys(workspaceId, academicYear, termNumber, gradeId, scheduleSlotKeys),
    scopedPromise,
    fixedPromise,
    programCellsPromise,
  ]);

  const [presentationEntries, worksheetEntries] = await Promise.all([
    fetchPresentationAuditEntriesForProgramScope({
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      programProgressCells,
      scopedRows,
      fixedRows,
    }),
    fetchWorksheetAuditEntriesForProgramScope({
      workspaceId,
      programProgressCells,
      scopedRows,
      fixedRows,
    }),
  ]);

  if (scopeSig) {
    saveProgramPresentationAuditSnapshotCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeSig,
      {
        presentationEntries,
        scopedRowIds: scopedRows.map((row) => row.id.trim()).filter(Boolean),
        fixedRowIds: fixedRows.map((row) => row.id.trim()).filter(Boolean),
        programCellIds: programProgressCells.map((cell) => cell.id.trim()).filter(Boolean),
      }
    );
  }

  return {
    annualTimetableCells,
    scopedRows,
    fixedRows,
    programProgressCells,
    presentationEntries,
    worksheetEntries,
  };
}
